import {
  TEMPLATE_TYPE_LABEL,
  TEMPLATE_TYPES,
  type TemplateTypeValue,
} from '@/modules/photo-report-templates';

/**
 * The job detail's main-column tabs. Overview leads and is the default — it's
 * the one tab every job has, whatever its status. The two reports a job can carry
 * (`photo_report` / `jac`, DERP-168) sit at the same level as Photos & docs and
 * Cost summary rather than nested under a "Reports" tab: they're what a
 * technician came to fill in, so they shouldn't cost an extra click, and a
 * second tab row is expensive on a phone.
 *
 * Report tab values are the API's `template_type` verbatim, so the URL, the tab
 * id, and the `by-project?template_type=` query all speak one vocabulary.
 */
export type JobDetailTab = 'overview' | TemplateTypeValue | 'photos' | 'cost';

/**
 * Report tabs, in display order — the template types themselves. Only shown
 * once the job is under way. Not re-listed here: a type the templates module
 * knows about but this strip forgot would be a tab a technician can never
 * reach.
 */
export const REPORT_TAB_VALUES = TEMPLATE_TYPES;

const ALL_TABS: readonly JobDetailTab[] = ['overview', ...REPORT_TAB_VALUES, 'photos', 'cost'];

/**
 * Tab labels. The two report tabs take their names from the templates module,
 * which owns `TemplateTypeValue` — a report is called the same thing here, in
 * the templates list, and in the fill surface's empty state.
 */
export const JOB_DETAIL_TAB_LABELS: Record<JobDetailTab, string> = {
  overview: 'Overview',
  ...TEMPLATE_TYPE_LABEL,
  photos: 'Photos & docs',
  cost: 'Cost summary',
};

/** Narrow a tab to a report type, so the fill surface gets a real `template_type`. */
export function isReportTab(tab: JobDetailTab): tab is TemplateTypeValue {
  return tab in TEMPLATE_TYPE_LABEL;
}

/**
 * Parse the URL's `?tab=`. Unknown values yield `undefined` so the component
 * falls back to Overview; resolution stays there because whether a report tab
 * is even on offer depends on the job, which the route doesn't have.
 */
export function parseJobDetailTab(value: unknown): JobDetailTab | undefined {
  return ALL_TABS.find((tab) => tab === value);
}
